import type { BigNumber } from "./BigNumber.js";
import type { DisplayMode, FormatOptions, FormatStyle } from "./types.js";
import {
  ShortScaleDisplayMode,
  AlphabetDisplayMode,
  ArabicDisplayMode,
} from "./display/powerOfThousand.js";
import { IndianDisplayMode, ThaiDisplayMode } from "./display/digitUnit.js";
import { MyriadDisplayMode } from "./display/myriad.js";
import { myriadUnitsFor, isEastAsianLocale } from "./display/units.js";

// 형식별 단일 인스턴스 (불변·무상태라 재사용).
const SHORT_SCALE = new ShortScaleDisplayMode();
const ALPHABET = new AlphabetDisplayMode();
const ARABIC = new ArabicDisplayMode();
const INDIAN = new IndianDisplayMode();
const THAI = new ThaiDisplayMode();
// 동양 모드는 units 배열(모듈 상수, 참조 고유) 키로 캐시 — locale 분기 SSoT는 units.ts `myriadUnitsFor`.
const MYRIAD_CACHE = new Map<readonly string[], MyriadDisplayMode>();

function myriadFor(locale: string | undefined): DisplayMode {
  const units = myriadUnitsFor(locale);
  let mode = MYRIAD_CACHE.get(units);
  if (!mode) {
    mode = new MyriadDisplayMode(units);
    MYRIAD_CACHE.set(units, mode);
  }
  return mode;
}

/** locale 자동 — 언어권별 관습 표기로 위임 (동아시아 myriad / 인도 / 태국 / 아랍 / 그 외 short-scale). */
function resolveLocale(locale: string | undefined): DisplayMode {
  if (isEastAsianLocale(locale)) return myriadFor(locale);
  const lc = (locale ?? "").toLowerCase();
  if (lc.startsWith("hi")) return INDIAN;
  if (lc.startsWith("th")) return THAI;
  if (lc.startsWith("ar")) return ARABIC;
  return SHORT_SCALE;
}

function resolveMode(style: FormatStyle, locale: string | undefined): DisplayMode {
  switch (style) {
    case "kmg":
      return SHORT_SCALE;
    case "alphabet":
      return ALPHABET;
    case "myriad":
      return myriadFor(locale);
    case "indian":
      return INDIAN;
    case "thai":
      return THAI;
    case "arabic":
      return ARABIC;
    case "locale":
      return resolveLocale(locale);
  }
}

/** BigNumber → 표시 문자열. style + locale + decimals(기본 1)로 형식 결정. */
export function format(value: BigNumber, opts: FormatOptions): string {
  const decimals = opts.decimals ?? 1;
  return resolveMode(opts.style, opts.locale).format(value, decimals);
}
